export type TrustComplaintStatus = "new" | "reviewed" | "in_progress" | "closed";

export const TRUST_COMPLAINT_STATUS_LABELS: Record<TrustComplaintStatus, string> = {
  new: "New",
  reviewed: "Reviewed",
  in_progress: "In progress",
  closed: "Closed",
};

export const TRUST_COMPLAINT_STATUS_ORDER: TrustComplaintStatus[] = [
  "new",
  "reviewed",
  "in_progress",
  "closed",
];

export const TRUST_COMPLAINT_OPEN_STATUSES: TrustComplaintStatus[] = ["new", "reviewed", "in_progress"];

export type TrustComplaintPhoto = {
  id: number;
  filename: string;
  content_type: string;
  byte_size: number;
  url: string;
};

export type TrustComplaintAddressData = {
  address?: string;
  formatted_address?: string;
  [key: string]: unknown;
};

export type TrustComplaint = {
  id: number;
  complaint: string;
  submitted_at: string;
  updated_at: string;
  status: TrustComplaintStatus;
  assigned_to_id: number | null;
  assigned_to_name: string | null;
  internal_notes: string | null;
  contact_name: string | null;
  contact_email: string | null;
  resident_id: number | null;
  resident?: {
    id: number;
    display_name: string;
    email: string | null;
    phone: string | null;
  } | null;
  source: string | null;
  photo_count: number;
  photos: TrustComplaintPhoto[];
  address_data?: TrustComplaintAddressData;
  contact_phone: string | null;
  incident_latitude: number | null;
  incident_longitude: number | null;
  incident_location_description: string | null;
  text_moderation: TrustComplaintTextModeration | null;
};

// Written by the moderation gate (ENG-2391); null until a concern has been checked.
export type TrustComplaintTextModeration = {
  model: string;
  flagged: boolean;
  categories: Record<string, boolean>;
  category_scores: Record<string, number>;
  checked_at: string;
};

export type TrustComplaintPayload = {
  status?: TrustComplaintStatus;
  assigned_to_id?: number | null;
  internal_notes?: string | null;
  resident_id?: number | null;
};

// Full-dataset counts for the municipality with search and source applied, never queue or status.
export type TrustComplaintCounts = {
  open: number;
  closed: number;
  all: number;
  by_status: Record<TrustComplaintStatus, number>;
};

export type TrustComplaintsResponse = {
  complaints: TrustComplaint[];
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
  next_page: number | null;
  counts: TrustComplaintCounts;
};

// Omit the queue to list every concern.
export type TrustComplaintQueue = "open" | "closed";

export type ListTrustComplaintsParams = {
  page?: number;
  search?: string;
  source?: string;
  queue?: TrustComplaintQueue;
  status?: TrustComplaintStatus;
  signal?: AbortSignal;
};

const jsonHeaders = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
}

function trustComplaintsBasePath(municipalityId: number): string {
  return `/api/v1/municipalities/${municipalityId}/trust_complaints`;
}

export async function listTrustComplaints(
  municipalityId: number,
  params: ListTrustComplaintsParams = {},
): Promise<TrustComplaintsResponse> {
  const searchParams = new URLSearchParams();
  if (params.page != null) searchParams.set("page", String(params.page));
  if (params.search?.trim()) searchParams.set("search", params.search.trim());
  if (params.source?.trim()) searchParams.set("source", params.source.trim());
  if (params.queue) searchParams.set("queue", params.queue);
  if (params.status) searchParams.set("status", params.status);

  const query = searchParams.toString();
  const url = query
    ? `${trustComplaintsBasePath(municipalityId)}?${query}`
    : trustComplaintsBasePath(municipalityId);

  const response = await fetch(url, {
    credentials: "include",
    signal: params.signal,
  });

  if (!response.ok) {
    throw new Error("Failed to load trust complaints");
  }

  return response.json() as Promise<TrustComplaintsResponse>;
}

export async function getTrustComplaint(
  municipalityId: number,
  complaintId: number,
  signal?: AbortSignal,
): Promise<TrustComplaint> {
  const response = await fetch(`${trustComplaintsBasePath(municipalityId)}/${complaintId}`, {
    credentials: "include",
    signal,
  });

  if (!response.ok) {
    throw new Error("Failed to load trust complaint");
  }

  return response.json() as Promise<TrustComplaint>;
}

export async function updateTrustComplaint(
  municipalityId: number,
  complaintId: number,
  patch: TrustComplaintPayload,
): Promise<TrustComplaint> {
  const response = await fetch(`${trustComplaintsBasePath(municipalityId)}/${complaintId}`, {
    method: "PATCH",
    credentials: "include",
    headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({ trust_complaint: patch }),
  });

  if (!response.ok) {
    throw new Error("Failed to update trust complaint");
  }

  return response.json() as Promise<TrustComplaint>;
}
